// Bloques B/E/F/G/I de 019 (Fase 1B, Tanda 16, ver PLAN.md) — cierra 019
// completo y, con eso, toda la Fase 1B. Bloques A/C/D/H ya existen (A vía
// `EncabezadoMspCampos`, C/D/H de la Tanda 15).
//
// Todo acá es `Par[]` puro salvo el diagnóstico de B (crea `Condition`,
// manejado en `Formulario019.tsx` con `SelectorDiagnosticoCie10` — mismo
// patrón que los 2 diagnósticos de 018A/Tanda 14, sin rollback si falla).
//
// Decisiones de diseño (ambigüedad de producto sin impacto clínico, PLAN.md
// no las resuelve — se deciden y se marcan, mismo criterio de toda la Fase
// 1B):
// - E/F (ingresos/eliminación) no se persisten como `Observation` — mismo
//   criterio que H/I de 018A: sin consumidor real hoy. El total de cada uno
//   se calcula, no se pide como input.
// - G (evolución postanestésica): el papel (hoja 019 (2)) trae 39 renglones con FECHA/HORA/EVOLUCIÓN/PRESCRIPCIÓN/ADMINISTRACIÓN
//   FÁRMACOS (contados sobre el escaneo); el tope de filas es ese número. Antes eran 10 (la fuente no se había medido).
// - I (profesional que autoriza el egreso): no se captura — lo arma el
//   servidor desde el firmante (T6b), ver más abajo.
import type { Par } from './index';
import type { Traducir } from './tecnicaAnestesicaDatos';

// ---------- B — Procedimiento (sin el diagnóstico, ver arriba) ----------

export const TECNICA_ANESTESICA_019_OPCIONES = ['general', 'local', 'troncular', 'neuroaxial', 'intravenosa', 'sedacion'] as const;

export interface EstadoBloqueB019 {
  readonly especialidad: string;
  readonly cirugiaRealizada: string;
  readonly tecnicaAnestesica: Readonly<Record<string, boolean>>;
}

export function estadoInicialBloqueB019(): EstadoBloqueB019 {
  return { especialidad: '', cirugiaRealizada: '', tecnicaAnestesica: {} };
}

function paresDeBloqueB019(t: Traducir, e: EstadoBloqueB019): Par[] {
  return [
    { pregunta: t('formularios.f019.bloqueB.especialidad'), respuesta: e.especialidad.trim() },
    { pregunta: t('formularios.f019.bloqueB.cirugiaRealizada'), respuesta: e.cirugiaRealizada.trim() },
    {
      pregunta: t('formularios.f019.bloqueB.tecnicaAnestesica'),
      respuesta: TECNICA_ANESTESICA_019_OPCIONES.filter((op) => e.tecnicaAnestesica[op]).map((op) => t(`formularios.f019.bloqueB.tecnicaAnestesicaOpciones.${op}`)).join(', '),
    },
  ];
}

// ---------- E — Ingresos / F — Eliminación (totales calculados, sin persistir) ----------

export const CAMPOS_INGRESOS = ['oral', 'infusiones', 'sangreDerivados', 'otros'] as const;
export type CampoIngresos = (typeof CAMPOS_INGRESOS)[number];
export type Ingresos = Record<CampoIngresos, string>;

export function estadoInicialIngresos(): Ingresos {
  return Object.fromEntries(CAMPOS_INGRESOS.map((c) => [c, ''])) as Ingresos;
}

export const CAMPOS_ELIMINACION = ['diuresisSondaVesical', 'vomitoSng', 'toracico', 'drenajes', 'otras'] as const;
export type CampoEliminacion = (typeof CAMPOS_ELIMINACION)[number];
export type Eliminacion = Record<CampoEliminacion, string>;

export function estadoInicialEliminacion(): Eliminacion {
  return Object.fromEntries(CAMPOS_ELIMINACION.map((c) => [c, ''])) as Eliminacion;
}

function sumaMl(valores: readonly string[]): number {
  return valores.reduce((acc, v) => acc + (Number(v) || 0), 0);
}

function paresDeIngresosYEliminacion(t: Traducir, ingresos: Ingresos, eliminacion: Eliminacion): Par[] {
  const totalIngresos = sumaMl(CAMPOS_INGRESOS.map((c) => ingresos[c]));
  const totalEliminacion = sumaMl(CAMPOS_ELIMINACION.map((c) => eliminacion[c]));
  return [
    ...CAMPOS_INGRESOS.map((c) => ({ pregunta: t(`formularios.f019.bloqueE.campos.${c}`), respuesta: ingresos[c].trim() })),
    { pregunta: t('formularios.f019.bloqueE.total'), respuesta: totalIngresos > 0 ? String(totalIngresos) : '' },
    ...CAMPOS_ELIMINACION.map((c) => ({ pregunta: t(`formularios.f019.bloqueF.campos.${c}`), respuesta: eliminacion[c].trim() })),
    { pregunta: t('formularios.f019.bloqueF.total'), respuesta: totalEliminacion > 0 ? String(totalEliminacion) : '' },
  ];
}

// ---------- G — Evolución postanestésica (tabla repetible) ----------

export interface FilaEvolucion {
  readonly id: string;
  readonly fecha: string;
  readonly hora: string;
  readonly evolucion: string;
  readonly prescripcion: string;
  readonly administracionFarmacos: string;
}

export function filaEvolucionVacia(): FilaEvolucion {
  return { id: crypto.randomUUID(), fecha: '', hora: '', evolucion: '', prescripcion: '', administracionFarmacos: '' };
}

/** Renglones del bloque G en el papel de 019 (39). */
export const TOPE_FILAS_EVOLUCION = 39;

// ---------- I — Profesional que autoriza el egreso ----------
// Ya NO se teclea (T6b, 2026-09-24): el papel (hoja 019 (2), bloque I) pide UN solo profesional con fecha, hora, nombres, apellidos y
// número de documento; los arma el servidor al firmar (nombre del firmante de la sesión + cédula del panel de firma, ver
// `TIPOS_CON_CEDULA`). Los documentos ya firmados con esos campos conservan sus pares tal cual: solo se dejó de capturar.

// ---------- Estado combinado B(sin diagnóstico)/E/F/G/I ----------

export interface EstadoBloquesRestantes019 {
  readonly b: EstadoBloqueB019;
  readonly ingresos: Ingresos;
  readonly eliminacion: Eliminacion;
  readonly evolucion: readonly FilaEvolucion[];
}

export function estadoInicialBloquesRestantes019(): EstadoBloquesRestantes019 {
  return {
    b: estadoInicialBloqueB019(),
    ingresos: estadoInicialIngresos(),
    eliminacion: estadoInicialEliminacion(),
    evolucion: [],
  };
}

/** Serializa B(sin diagnóstico)/E/F/G/I a `Par[]`, listos para `onGuardado`. */
export function paresDeBloquesRestantes019(t: Traducir, estado: EstadoBloquesRestantes019): Par[] {
  const pares: Par[] = [
    ...paresDeBloqueB019(t, estado.b),
    ...paresDeIngresosYEliminacion(t, estado.ingresos, estado.eliminacion),
    ...estado.evolucion.map((f, i) => ({
      pregunta: `${t('formularios.f019.bloqueG.titulo')} #${i + 1}`,
      respuesta: [f.fecha, f.hora, f.evolucion, f.prescripcion, f.administracionFarmacos].filter((v) => v.trim() !== '').join(' | '),
    })),
  ];
  return pares.filter((p) => p.respuesta.trim() !== '');
}
